import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MSkeleton } from '@Components/Base';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';

import {
  ApprovalHistory,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { PresentationContainer, ViewResolver } from '@Components/index';
import { HrmRequestFormResponse } from '@Types/api/form';
import { RequestList } from '@Components/RequestList';
import useStatusFinderByNum from '@Hooks/useStatusFinderByNum';
import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetApprovalHistoryMutation,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import { modalActions } from '@Store/slices/modalSlice';
import {
  useAppDispatch,
  useAppSelector,
  useIsLoadedConent,
  useRequestRefetcher,
} from '@Hooks/index';
import { getModalStatus } from '@Store/selectors/modalSelector';
import { NotificationContext } from '@Context/NotificationContext';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { SortConfig } from '@Features/types/SortConfig';
import { FiltersWidget } from '@Components/FiltersWidget';
import { ViewWidget } from '@Components/ViewWidget';
import { useFormik } from 'formik';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import {
  getResumptionStatusesDict,
  getResumptionWorkShiftDict,
} from '@Store/selectors/dictionarySelector';
import { SearchCriteria } from '@Types/api/common/filters';
import { prepareSearchCriterias } from '@Features/utils/prepareSearchCriterias';
import { isFiltersEmptySelector } from '@Store/selectors/filterOptionsSelector';
import { ApprovalHistory as ApprovalHistoryModal } from '@Components/ApprovalHistory';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';
import { TypeDict } from '@Types/index';
import Filters from './search/Filter';
import { mapColumns } from './list/MapColumns';
import { dataSourceMap } from './list/DataSource';
import RequestViewForm from './forms/View/RequestViewForm';
import {
  initialValues as filtersInitialsValues,
  filterTypes,
  getFiltersMapper,
} from './search/filters.utils';
import { ApplyFiltersHandler } from './search/types';
import RequestViewMobileForm from './forms/View/mobile/RequestViewMobileForm';

const ResumptionRequests: React.FC = () => {
  const {
    deleteSuccessNotification,
    errorNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isRequestFormPagePrev = useRef(isRequestFormPage);

  const isFiltersEmpty = useAppSelector(isFiltersEmptySelector);
  const statuses = useAppSelector(getResumptionStatusesDict);
  const isViewWidgetOpen = useAppSelector(getModalStatus);

  const [tagsValues, setTagsValues] = useState(filtersInitialsValues);
  const [showTags, setShowTags] = useState(false);
  const [showFiltersError, setShowFiltersError] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [sorting, setSorting] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<SearchCriteria[] | undefined>(
    undefined
  );
  const [isApprovalHistoryOpen, setIsApprovalHistoryOpen] = useState(false);
  const [approvalHistoryData, setApprovalHistoryData] = useState<
    ApprovalHistory[] | null
  >(null);

  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const { findStatus } = useStatusFinderByNum('Resumption');
  const [submitRequest] = useSubmitHrmRequestMutation();
  const [cancelRequest] = useCancelHrmRequestMutation();
  const [getApprovalHistory] = useGetApprovalHistoryMutation();
  const resumptionWorkShifts = useAppSelector(getResumptionWorkShiftDict);

  const closeFilters = (): void => {
    setIsFiltersOpen(false);
  };

  const openFilters = (): void => {
    setIsFiltersOpen(true);
  };

  const handleApplyFilters: ApplyFiltersHandler = (options): void => {
    const filtersMapper = getFiltersMapper(statuses, resumptionWorkShifts);
    const searchCriterias = prepareSearchCriterias(
      options,
      filterTypes,
      filtersMapper
    );
    setFilters(searchCriterias);

    const parsedOptions = parseSelectedFiltersToOptions(options);

    // true if filters was empty and empty after submit
    const isFiltersStillEmpty = isFiltersEmpty && !parsedOptions.length;
    setShowFiltersError(isFiltersStillEmpty);

    if (!isFiltersStillEmpty) {
      closeFilters();
    }

    dispatch(filterOptionsActions.setFilterSettings(searchCriterias));
    dispatch(filterOptionsActions.setFilterOptions(parsedOptions));
    dispatch(eventMessageActions.sendEventType('FILTER'));

    setShowTags(true);
    setTagsValues(options);
  };

  const formik = useFormik({
    initialValues: filtersInitialsValues,
    enableReinitialize: true,
    onSubmit: handleApplyFilters,
  });

  const clearParams = (): void => {
    setSorting(null);
    setFilters(undefined);
    setSearchValue(undefined);
    setTagsValues(filtersInitialsValues);
    formik.setValues(filtersInitialsValues);
  };

  const {
    currentPage,
    fetchRequest,
    handleLoadMore,
    onChangeCurrentPage,
    isSuccess,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'Resumption',
    searchValue,
    sorting,
  });
  const isContentLoaded = useIsLoadedConent(isSuccess);

  useEffect(() => {
    dispatch(filterOptionsActions.setFilterOptions(null));
  }, []);

  const handleSort = useCallback(
    (sortConfig: SortConfig | null) => {
      fetchRequest(TABLE_PAGE_DEFAULT, sortConfig);

      onChangeCurrentPage(TABLE_PAGE_DEFAULT);
      setSorting(sortConfig);
    },
    [fetchRequest]
  );

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  const handleChangePage = useCallback((page: number): void => {
    onChangeCurrentPage(page);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  const handleViewRecord = useCallback((recordKey: string): void => {
    dispatch(
      modalActions.setStatusModal({
        status: true,
        key: String(recordKey),
      })
    );
  }, []);

  const handleSubmitRequest = useCallback(
    (requestNo: string): void => {
      submitRequest(String(requestNo))
        .unwrap()
        .then(() => {
          submitSuccessNotification(String(requestNo));

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [fetchRequest]
  );

  const handleEditRecord = useCallback(
    (recordKey: string): void => {
      navigate(`${pathname}/edit/${recordKey}`);
    },
    [pathname]
  );

  const handleDeleteRequest = useCallback(
    (requestNo: string): void => {
      const reqId = String(requestNo);

      deleteRequest(reqId)
        .unwrap()
        .then(() => {
          deleteSuccessNotification(reqId);

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [fetchRequest]
  );

  const handleCloseViewWidget = (): void => {
    dispatch(modalActions.setStatusModal({ status: false, key: '' }));
  };

  const handleCancelRequest = useCallback(
    (requestNo: string) => {
      const reqId = String(requestNo);

      cancelRequest(reqId)
        .unwrap()
        .then(() => {
          cancelSuccessNotification(reqId);

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });

      handleCloseViewWidget();
    },
    [fetchRequest]
  );

  const findWorkShiftLabel = (workShift: number): TypeDict | undefined =>
    resumptionWorkShifts.find((shift) => shift.codeNum === workShift);

  const dataSource = useMemo(
    () =>
      dataSourceMap(
        listRequests as RequestListSearchResponse<HrmRequestFormResponse>,
        findStatus,
        findWorkShiftLabel
      ),
    [listRequests, findStatus]
  );

  const handleSearch = useCallback((value: string | null) => {
    setSearchValue(value || undefined);
    dispatch(eventMessageActions.sendEventType('SEARCH'));
  }, []);

  const handleApprovalHistory = useCallback((requestNo: string) => {
    getApprovalHistory(requestNo)
      .unwrap()
      .then((approvalHistoryResp) => {
        if (Array.isArray(approvalHistoryResp) && approvalHistoryResp.length) {
          setIsApprovalHistoryOpen(true);
          setApprovalHistoryData(approvalHistoryResp);
        }
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, []);

  const tagsFormik = {
    ...formik,
    values: tagsValues,
  };

  return (
    <PresentationContainer fullHeight={isRequestFormPage}>
      {isRequestFormPage ? (
        <Outlet />
      ) : (
        <>
          <FiltersWidget
            onSearchSubmit={handleSearch}
            isOpen={isFiltersOpen}
            onClose={closeFilters}
            onOpen={openFilters}
            formik={tagsFormik}
            showTags={showTags}
          >
            <Filters showError={showFiltersError} formik={formik} />
          </FiltersWidget>
          {isContentLoaded ? (
            <RequestList
              onLoadMore={handleLoadMore}
              onEdit={handleEditRecord}
              onView={handleViewRecord}
              onSubmit={handleSubmitRequest}
              onDelete={handleDeleteRequest}
              onCancel={handleCancelRequest}
              onApprovalHistory={handleApprovalHistory}
              onChangePage={handleChangePage}
              currentPage={currentPage}
              onSort={handleSort}
              requestsDataList={
                requestsDataList as Array<HrmRequestFormResponse>
              }
              listRequests={
                listRequests as RequestListSearchResponse<HrmRequestFormResponse>
              }
              mapColumns={mapColumns}
              dataSource={dataSource}
              requestType="Resumption"
              isFiltersApplied={Boolean(filters)}
              isSearchApplied={Boolean(searchValue)}
            />
          ) : (
            <MSkeleton />
          )}
          <ViewWidget isOpen={isViewWidgetOpen} onClose={handleCloseViewWidget}>
            <ViewResolver
              desktop={<RequestViewForm />}
              mobile={
                <RequestViewMobileForm
                  onCancelRequest={handleCancelRequest}
                  onDeleteRequest={handleDeleteRequest}
                  onEdit={handleEditRecord}
                  onSubmitRequest={handleSubmitRequest}
                />
              }
            />
          </ViewWidget>
          <ApprovalHistoryModal
            isOpen={isApprovalHistoryOpen}
            onClose={() => {
              setIsApprovalHistoryOpen(false);
            }}
            data={approvalHistoryData}
            users={users}
          />
        </>
      )}
    </PresentationContainer>
  );
};

export default ResumptionRequests;
